import { WheelKind } from 'models/wheel-of-fortune/i-wheel-of-fortune';

/**
 * Centralized React Query keys for better cache management and consistency
 *
 * Structure:
 * - Use arrays for hierarchical keys: ['entity', 'action', ...params]
 * - Use objects for better organization and type safety
 * - Keep all query keys in one place for easy management
 */

export const queryKeys = {
  // Daily Deals
  dailyDeals: {
    all: ['daily-deals'] as const,
    list: () => [...queryKeys.dailyDeals.all, 'list'] as const,
    assets: (assetIds: number[]) => [...queryKeys.dailyDeals.all, 'assets', assetIds] as const,
  },

  // Store Packages
  storePackages: {
    all: ['store-packages'] as const,
    list: () => [...queryKeys.storePackages.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.storePackages.all, 'detail', id] as const,
  },

  // World NFT
  worldNft: {
    all: ['world-nft'] as const,
    trade: () => [...queryKeys.worldNft.all, 'trade'] as const,
    assets: (tokenIds: number[]) => [...queryKeys.worldNft.all, 'assets', tokenIds] as const,
    yield: (tokenId: number) => [...queryKeys.worldNft.all, 'yield', tokenId] as const,
  },

  // Assets (general)
  assets: {
    all: ['assets'] as const,
    list: (ids: number[]) => [...queryKeys.assets.all, 'list', ids] as const,
    count: (tokenId: string, walletAddress: string) =>
      [...queryKeys.assets.all, 'count', tokenId, walletAddress] as const,
  },

  // Events
  event: {
    all: ['event'] as const,
    detail: (eventId: string) => [...queryKeys.event.all, 'detail', eventId] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.event.all, 'list', filters] as const,
    userStatus: (eventId: string) => [...queryKeys.event.all, 'userStatus', eventId] as const,
    userMetrics: (eventId: string) => [...queryKeys.event.all, 'userMetrics', eventId] as const,
    rewardHistory: (eventId: string, page: number) =>
      [...queryKeys.event.all, 'rewardHistory', eventId, page] as const,
  },

  // Daily Rewards
  dailyRewards: {
    all: ['daily-rewards'] as const,
    calendar: (year: number, month: number) => [...queryKeys.dailyRewards.all, 'calendar', year, month] as const,
    history: (page: number) => [...queryKeys.dailyRewards.all, 'history', page] as const,
  },

  // VIP Points
  vipPoints: {
    all: ['vip-points'] as const,
balance: (walletAddress: string, selectedWallets: string[] = []) =>
      [...queryKeys.vipPoints.all, 'balance', walletAddress, selectedWallets] as const,
    history: (page: number, pageSize: number) =>
      [...queryKeys.vipPoints.all, 'history', page, pageSize] as const,
    purchaseHistory: (page: number, pageSize: number) =>
      [...queryKeys.vipPoints.all, 'purchaseHistory', page, pageSize] as const,
  },

  // VIP Levels
  vipLevels: {
    all: ['vip-levels'] as const,
    list: (walletAddress = '') => [...queryKeys.vipLevels.all, 'list', walletAddress] as const,
  },

  // VIP User
  vipUser: {
    all: ['vip-user'] as const,
    data: (walletAddress: string) => [...queryKeys.vipUser.all, 'data', walletAddress] as const,
  },

  // VIP Royalty → Points preview
  vipRoyaltyPoints: {
    all: ['vip-royalty-points'] as const,
    // Per-user estimate behind an authenticated, 10/min-throttled endpoint — the wallet is in
    // the key so switching wallets never shows one holder's royalty figure to another.
    preview: (walletAddress: string) => [...queryKeys.vipRoyaltyPoints.all, 'preview', walletAddress] as const,
  },

  // VIP Shop
  vipShop: {
    all: ['vip-shop'] as const,
    offers: (walletAddress: string) => [...queryKeys.vipShop.all, 'offers', walletAddress] as const,
    status: (walletAddress: string) => [...queryKeys.vipShop.all, 'status', walletAddress] as const,
    purchaseHistory: (page: number, pageSize: number) =>
      [...queryKeys.vipShop.all, 'purchaseHistory', page, pageSize] as const,
  },

  // Cumulative Login
  cumulativeLogin: {
    all: ['cumulative-login'] as const,
    streak: (walletAddress: string) => [...queryKeys.cumulativeLogin.all, 'streak', walletAddress] as const,
    // The ladder is admin config, but the endpoint filters it to the caller's audience (non-VIP
    // bands when unauthenticated), so the wallet belongs in the key for the same reason as
    // `vipLevels.list` — otherwise the anonymous ladder stays cached after connecting.
    bands: (walletAddress = '') => [...queryKeys.cumulativeLogin.all, 'bands', walletAddress] as const,
  },

  // VIP Rewards (daily + level milestones)
  vipRewards: {
    all: ['vip-rewards'] as const,
    dailyStatus: (walletAddress: string) => [...queryKeys.vipRewards.all, 'daily-status', walletAddress] as const,
    levelStatus: (walletAddress: string) => [...queryKeys.vipRewards.all, 'level-status', walletAddress] as const,
    history: (page: number, pageSize: number) =>
      [...queryKeys.vipRewards.all, 'history', page, pageSize] as const,
    // Public, wallet-agnostic teaser for not-connected users (no per-wallet key).
    teaser: () => [...queryKeys.vipRewards.all, 'teaser'] as const,
  },

  // Wheel of Fortune
  wheelOfFortune: {
    all: ['wheel-of-fortune'] as const,
    // Keyed by wheel: the two wheels have separate segments, balances and spin numbers,
    // so a shared key would serve the regular wheel's payload to the VIP one on switch.
    data: (kind: WheelKind = 'regular') => [...queryKeys.wheelOfFortune.all, 'data', kind] as const,
    // History is unified across both wheels, so it is deliberately NOT keyed by kind.
    history: (page: number, pageSize: number) =>
      [...queryKeys.wheelOfFortune.all, 'history', page, pageSize] as const,
  },

  // NFT Spotlight History (NFT of the Day/Week featured records)
  nftSpotlightHistory: {
    all: ['nft-spotlight-history'] as const,
    list: (days: number) => [...queryKeys.nftSpotlightHistory.all, 'list', days] as const,
  },

  // NFT Yields (conservative yield calculation data)
  nftYields: {
    all: ['nft-yields'] as const,
    list: () => [...queryKeys.nftYields.all, 'list'] as const,
  },

  // User owned shares (for marketplace "own X shares" badge)
  userOwnedShares: {
    all: ['user-owned-shares'] as const,
    byWallet: (walletAddress: string) => [...queryKeys.userOwnedShares.all, walletAddress] as const,
  },

  // Empire (portfolio summary)
  empire: {
    all: ['empire'] as const,
    data: (walletAddress: string, selectedFilter = '') => [...queryKeys.empire.all, 'data', walletAddress, selectedFilter] as const,
  },

  // Dashboard
  dashboard: {
    all: ['dashboard'] as const,
    market: () => [...queryKeys.dashboard.all, 'market'] as const,
  },

  // Bid2Earn (auctions listing for the events page — cached, read-only)
  bid2earn: {
    all: ['bid2earn'] as const,
    auctions: () => [...queryKeys.bid2earn.all, 'auctions'] as const,
    metadata: (tokenIds: string[]) => [...queryKeys.bid2earn.all, 'metadata', tokenIds] as const,
  },
} as const;
